import { Document } from "./Document";
import { BuyerBriefRef } from "./BuyerBriefTypes";
import { OrderForm } from "./OrderFormTypes";
import { Tactic } from "./TacticTypes";
import { User } from "./UserGroup";

// File types that can be uploaded. Mirrors the server's UploadType enum
// (types/Document.ts): anything accepted here must be accepted there.
export const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".ppt",
    ".pptx",
    ".txt",
    ".rtf",
    ".md",
    ".csv",
    ".odt",
    ".ods",
    ".odp",
    ".zip",
];

// Whether a filename's extension is one we accept for upload.
export const hasAllowedExtension = (fileName: string): boolean => {
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    return allowedExtensions.includes(fileExt);
};

// Project

export enum ProjectType {
    DEAL = "deal",
    RFX = "rfx",
}

/**
 * The specific analysis a guided-intake draft requests — picked on the
 * wizard's kind step, recorded on the analysis. Each kind belongs to one
 * ProjectType family (see kindToProjectType). Null on legacy drafts, which
 * keep the per-type intake behavior.
 */
export enum AnalysisKind {
    DEAL_PLAYBOOK = "deal_playbook",
    COMMERCIAL_ASSESSMENT = "commercial_assessment",
    RATE_CARD_ASSESSMENT = "rate_card_assessment",
    MSA_ASSESSMENT = "msa_assessment",
    RFI = "rfi",
    RFP = "rfp",
    RFQ = "rfq",
}

/** The project-type family an analysis kind belongs to. */
export function kindToProjectType(kind: AnalysisKind): ProjectType {
    switch (kind) {
        case AnalysisKind.RFI:
        case AnalysisKind.RFP:
        case AnalysisKind.RFQ:
            return ProjectType.RFX;
        default:
            return ProjectType.DEAL;
    }
}

export enum ProjectStatus {
    DRAFT = "draft",
    ACTIVE = "active",
    COMPLETED = "completed",
}

export interface ProjectCollaborator {
    user: User;
    canEdit: boolean;
}

export enum ProjectAction {
    VIEW = "view",
    EDIT = "edit",
    DELETE = "delete",
    REASSIGN = "reassign",
    SHARE = "share",
}

export interface ProjectDocument {
    id: number;
    document: Document;
    isDeleted: boolean;
    recordCreationDate: Date;
    creator: User;
}

/** Closing outcome for deal projects. */
export enum DealClosingOutcome {
    SIGNED = "signed",
    CANCELLED = "cancelled",
}

/** Closing outcome for RFX projects. */
export enum RfxClosingOutcome {
    COMPLETED = "completed",
}

/** Outcome of a completed project's closing analysis. */
export type ClosingOutcome = DealClosingOutcome | RfxClosingOutcome;

/** Map from ProjectType to valid closing outcomes. */

/** "COMPLETE" is a dummy type indicating the project is closed with no actual analysis output — render the input instead. */
export enum AnalysisType {
    DEAL = "deal_analysis",
    RFX = "rfx_analysis",
    ROI = "roi_analysis",
    COMPLETE = "complete",
}

export interface AnalysisSummary {
    type: AnalysisType;
    version: number;
    status: ProjectAnalysisStatus;
}

/**
 * Reference to the origin of a new project, supplied by the client on create.
 * Carries only what the server needs to identify and validate the link;
 * the rich `ProjectSource` returned on responses is built server-side.
 */
export enum ProjectSourceType {
    RFX = "rfx",
}

export interface RfxProjectSourceInput {
    type: ProjectSourceType.RFX;
    rfxVendorId: number;  // the ILRfxVendor row id
}

export type ProjectSourceInput = RfxProjectSourceInput;

/**
 * Origin of a project that was spawned from another, as returned by the
 * server on Project / ProjectSummary responses. Includes denormalized data
 * the UI needs to render the link without an extra round trip.
 */
export interface RfxProjectSource {
    type: ProjectSourceType.RFX;
    projectId: number;    // the parent RFx project id
    projectName: string;  // the parent RFx project name
    rfxVendorId: number;  // the ILRfxVendor row id
}

export type ProjectSource = RfxProjectSource;

/** Where a deal's negotiation stands, as ops report it on the order form. */
export enum NegotiationStatus {
    NOT_STARTED = "not_started",
    IN_PROGRESS = "in_progress",
    READY_TO_SIGN = "ready_to_sign",
    COMPLETE = "complete",
    CANCELLED = "cancelled",
}

/** Where an RFx stands, as reported to the user. */
export enum RfxStatus {
    NOT_STARTED = "not_started",
    IN_PROGRESS = "in_progress",
    SELECT_VENDOR = "select_vendor",
    COMPLETE = "complete",
}

export enum RfxVendorStatus {
    CANDIDATE = "candidate",
    SELECTED = "selected",
}

/** One evaluated vendor of an RFx, with selection state and optional spawned deal. */
export interface RfxVendor {
    id: number; // ILRfxVendor row id — use as `source.rfxVendorId` when spawning a deal
    vendorKey: string;
    vendorName: string;
    vendorHero?: string;
    status: RfxVendorStatus;
    dealProjectId?: number;
    dealProject?: ProjectSummary;
}

/** Lightweight project summary for list views. Pre-computes derived values to avoid loading full relation graphs. */
export interface ProjectSummary {
    id: number;
    name: string;
    type: ProjectType;
    status: ProjectStatus;
    latestAnalysis?: AnalysisSummary;
    /** Set only when status is COMPLETED and the latest analysis is a closing analysis. */
    closingOutcome?: ClosingOutcome;
    creatorId: number;
    creatorName: string;
    ownerId: number;
    ownerName?: string;
    recordCreationDate: Date;
    lastUpdateDate: Date;
    documentCount: number;
    /** Deals only: the order form's negotiation status. */
    negotiationStatus?: NegotiationStatus;
    /** RFx only, once an output was published. Absent before that: fall back to the project status. */
    rfxStatus?: RfxStatus;
    vendorKey?: string;
    vendorName?: string;
    vendorHero?: string;
    buyerBrief?: BuyerBriefRef; // the vendor's buyer brief, new or legacy, resolved by the server
    categoryPath?: string[]; // the project's category in the org's taxonomy, root first
    source?: ProjectSource;
    allowedActions?: ProjectAction[];
    collaboratorIds?: number[];
}

export interface Project {
    id: number;
    organizationId: number;
    name: string;
    type: ProjectType;
    status: ProjectStatus;
    creator: User;
    creatorId: number;
    owner?: User;
    ownerId: number;
    recordCreationDate: Date;
    recordUpdateDate: Date;
    lastUpdateDate: Date;
    submittedDate?: Date;
    completedDate?: Date;
    recordDeletionDate?: Date;
    orderForm?: OrderForm;
    tactic?: Tactic;
    documents?: ProjectDocument[];
    analyses: ProjectAnalysis[];
    collaborators?: ProjectCollaborator[];
    allowedActions?: ProjectAction[];
    closingOutcome?: ClosingOutcome;
    categoryPath?: string[]; // the project's category in the org's taxonomy, root first
    source?: ProjectSource;
    /** Present on RFx projects. */
    rfx?: Rfx;
}

/** RFx-specific data hanging off a `Project` when `type === RFX`. */
export interface Rfx {
    id: number;
    /** Set by ops on publish, complete once closed. */
    status: RfxStatus;
    vendorsSelected: boolean; // user-controlled "yes, vendor(s) were selected"
    vendors: RfxVendor[]; // one entry per evaluated vendor candidate
}

export function canDo(project: Project | ProjectSummary, action: ProjectAction): boolean {
    return project.allowedActions?.includes(action) ?? false;
}

/** Convert a full Project to a ProjectSummary for use in ProjectCard header of ProjectView. */
export function toProjectSummary(project: Project): ProjectSummary {
    return {
        id: project.id,
        name: project.name,
        type: project.type,
        status: project.status,
        closingOutcome: project.closingOutcome,
        creatorId: project.creatorId,
        creatorName: project.creator?.name || "Unknown",
        ownerId: project.ownerId,
        ownerName: project.owner?.name,
        recordCreationDate: project.recordCreationDate,
        lastUpdateDate: project.lastUpdateDate,
        latestAnalysis: project.status !== ProjectStatus.DRAFT
            ? (() => {
                const latest = getLatestAnalysis(project);
                return { type: latest.type, version: latest.version, status: latest.status };
            })()
            : undefined,
        documentCount: project.documents?.length ?? 0,
        negotiationStatus: project.orderForm?.negotiationStatus,
        rfxStatus: project.rfx?.status,
        vendorKey: project.orderForm?.vendorKey,
        vendorName: project.orderForm?.vendor?.name,
        vendorHero: project.orderForm?.vendor?.hero,
        buyerBrief: project.orderForm?.vendor?.buyerBrief,
        source: project.source,
        allowedActions: project.allowedActions,
        collaboratorIds: project.collaborators?.map((c) => c.user.id),
    };
}

export function isDealProject(project: Project | ProjectSummary): boolean {
    return project.type === ProjectType.DEAL;
}

export function isRfxProject(project: Project | ProjectSummary): boolean {
    return project.type === ProjectType.RFX;
}

export function getProjectTypeLabel(projectType: ProjectType): string {
    switch (projectType) {
        case ProjectType.DEAL:
            return "deal";
        case ProjectType.RFX:
            return "RFX";
    }
}

/** The ProjectAnalysis for the given version number, otherwise undefined. */
export function getAnalysisByVersion(project: Project, version: number): ProjectAnalysis {
    // Assume that all projects have at least one analysis.
    // Which is true since we create an analysis when we create a project.
    return project.analyses.find((a) => a.version === version)!;
}

export function getLatestAnalysis(project: Project): ProjectAnalysis {
    const version = Math.max(...project.analyses.map((a) => a.version));
    return getAnalysisByVersion(project, version);
}

export interface ProjectCreateRequest {
    name?: string;
    type: ProjectType;
    survey?: {
        schema: object;
        response: object;
    };
    files?: File[];
    /** Origin of this project when spawned from another (e.g. an RFx). */
    source?: ProjectSourceInput;
    /** If true, submit the first analysis immediately after creation. */
    submitImmediately?: boolean;
    /** Guided intake: mark the draft as a guided-intake session. */
    guidedIntake?: boolean;
    /** Guided intake: the analysis kind picked on the wizard. */
    analysisKind?: AnalysisKind;
}

export interface ProjectUpdateRequest {
    name?: string;
    type?: ProjectType;
    survey?: {
        schema: object;
        response: object;
    };
    ownerId?: number;
}

// Project Dashboard Data

// Deal Analysis Output

export enum DynamicLeverEvaluation {
    NOT_FAVORABLE = "Not Favorable",
    NEUTRAL = "Neutral",
    FAVORABLE = "Favorable",
    NOT_PRESENT = "Not Present",
}

export interface DynamicLever {
    name?: string;
    type?: string;
    presentInContract?: boolean; // whether the lever is present in the contract (which can be a favorable thing or not)
    evaluation?: DynamicLeverEvaluation;
    currentState?: string;
    isActionable: boolean; // whether the lever is NOT in good standing, i.e. actionable
    priority?: number; // priority for levers not in good standing
    optimalState?: string; // optimal state for levers not in good standing
    justification?: string; // justification for levers not in good standing
}

export enum Evaluation {
    NOT_FAVORABLE = "Not Favorable",
    NEUTRAL = "Neutral",
    FAVORABLE = "Favorable",
}

export type FeedbackValue = "good" | "bad";

export interface AnalysisFeedback {
    userId: number;
    date: string; // ISO date string
    value: FeedbackValue;
    comment?: string;
}

export enum AnalysisOutputType {
    DEAL = "deal_output",
    RFX = "rfx_output",
    FLEXIBLE = "flexible_output",
}

// ── Structured Output

export type DealInfo = {
    // Exactly one of `vendorKey` or `vendorName` must be supplied.
    // `vendorKey` references an existing `il_unified_vendor` row directly.
    // `vendorName` is the raw vendor name from contract extraction; the server
    // resolves it to a vendor (creating one if no substring match exists).
    vendorKey?: string;
    vendorName?: string;
    value: number; // in USD cents
    orderStartDate?: string; // ISO date string
    orderEndDate?: string; // ISO date string
    autoRenewal: boolean;
    orderScore: number;
    optimizedScore: number;
    ownerId?: number; // defaults to project owner
    increase?: number; // identified increase at renewal in USD cents
    estimatedSavingsLow?: number; // DEPRECATED - estimated savings (low end) in USD cents
    estimatedSavingsHigh?: number; // DEPRECATED - estimated savings (high end) in USD cents
    targetedSavings?: number; // total targeted savings in USD cents
    realizedSavings?: number; // realized savings in USD cents
    status?: NegotiationStatus; // set by ops on publish; absent leaves the deal's negotiation status as is
}

export enum StructuredOutputType {
    DEAL = "deal",
    RFX = "rfx",
}

export interface DealStructuredOutput {
    type: StructuredOutputType.DEAL;
    data: {
        dealInfo?: DealInfo,
        levers?: DynamicLever[];
    }
}

export interface RfxStructuredOutputData {
    vendors: Array<{
        vendorKey: string;
    }>;
    /** The RFx status as of this round; the cache takes it when the round is the latest. */
    status: RfxStatus;
}

export interface RfxStructuredOutput {
    type: StructuredOutputType.RFX;
    data: RfxStructuredOutputData;
}

export type StructuredOutput = DealStructuredOutput | RfxStructuredOutput;

// ── Flexible Output ──

export interface FlexibleAnalysisOutput {
    type: AnalysisOutputType.FLEXIBLE;
    content: string; // HTML for main content area (required)
    structuredOutputs?: StructuredOutput[];
}

// ── Legacy Output ──
 
export interface DealAnalysisOutput {
    type: AnalysisOutputType.DEAL;
    dealInfo?: Omit<DealInfo, "estimatedSavingsLow" | "estimatedSavingsHigh">;
    levers?: DynamicLever[]; // Dynamic Levers
    summary?: {
        text: string; // human-readable description of what happened this round
        estimatedSavings?: [number, number]; // estimated savings in USD cents (version 1 only)
        realizedSavings?: number; // realized savings in USD cents (version 2+ only)
        strategicProgress: {
            prioritiesIdentified: number;
            prioritiesAchieved?: number; // v2+ only
        };
        evaluation: {
            overall: Evaluation;
            pricing?: Evaluation;
            structuralFlexibility?: Evaluation;
            operationalAndLegalProtections?: Evaluation;
            strategicFit?: Evaluation;
        };
        implications: Array<{ category: string; implication: string }>;
        nextActions: Array<{ action: string; justification: string }>;
    };
    report?: {
        contractSummary: string; // HTML
        topLegalPriorities: string; // HTML
        negotiationStrategy: string; // HTML
    };
    sourceUrls?: {
        summary?: string; // Google Sheet URL for summary import
        report?: string; // Google Doc URL for report import
        levers?: string; // Google Sheet URL for levers import
    };
}

export interface RfxAnalysisOutput {
    type: AnalysisOutputType.RFX;
    report?: {
        content: string; // HTML
    };
    sourceUrls?: {
        report?: string; // Google Doc URL for report import
    };
    vendors?: Array<{
        vendorKey: string;
    }>;
}

export type AnalysisOutput = DealAnalysisOutput | RfxAnalysisOutput | FlexibleAnalysisOutput;

export function isDealOutput(output: AnalysisOutput | undefined): output is DealAnalysisOutput {
    return output?.type === AnalysisOutputType.DEAL;
}

export function isRfxOutput(output: AnalysisOutput | undefined): output is RfxAnalysisOutput {
    return output?.type === AnalysisOutputType.RFX;
}

export function isFlexibleOutput(output: AnalysisOutput | undefined): output is FlexibleAnalysisOutput {
    return output?.type === AnalysisOutputType.FLEXIBLE;
}

// Project Analysis

export enum ProjectAnalysisStatus {
    DRAFT = "draft",
    SUBMITTED = "submitted",
    REQUIRE_USER_ACTION = "require_user_action",
    COMPLETED = "completed",
}

export interface SurveySchema {
    schema: object;
    version: string;
}

export interface SurveyData {
    schema: object;
    response: object;
}

export interface ProjectAnalysisUpdateRequest {
    survey: {
        schema: object;
        response: object;
    };
}

export type InputDocumentChange = "added" | "removed";

export interface InputDocument {
    id: number;
    document: Document;
    change?: InputDocumentChange;
}

/**
 * How the user answers the remaining intake context after uploading documents:
 * `chat` is the free-form conversation (the agent asks follow-ups); `survey` is
 * a short fixed set of questions in a form. Chosen once and persisted.
 */
export enum IntakeMode {
    CHAT = "chat",
    SURVEY = "survey",
}

export interface ProjectAnalysisInput {
    revision: number;
    survey?: {
        schema: object;
        response: object;
    };
    surveySchemaVersion?: string;
    documents: InputDocument[];
    creator: User;
    recordCreationDate: Date;
    guidedIntake?: boolean; // Whether the user used the guided intake flow to create this project
    intakeMode?: IntakeMode;
    /** Guided intake: the chat transcript (text turns from the user and the agent). */
    messages?: { id: string; role: "user" | "system"; content: string }[];
}

export interface ProjectAnalysis {
    id: number;
    type: AnalysisType;
    version: number;
    status: ProjectAnalysisStatus;
    /** Whether the output carries a report to show; without one the analysis reads as pending. */
    hasReport?: boolean;
    requireUserActionMessage?: string | null;
    inputs?: ProjectAnalysisInput[];
    outputType?: AnalysisOutputType; // set when this analysis has a saved output; body is fetched lazily via getAnalysisOutput
    userFeedback?: Record<string, AnalysisFeedback[]>;
    creator: User;
    recordCreationDate: Date;
    recordUpdateDate: Date;
    submittedDate?: Date;
    completedDate?: Date;
    documents: Document[];
    attachments?: Document[];
}

/** Whether an analysis is a closing analysis (savings report or complete). */
export const isClosingAnalysis = (analysis: ProjectAnalysis): boolean =>
    analysis.type === AnalysisType.ROI || analysis.type === AnalysisType.COMPLETE;

/** Latest analysis status for a project or project summary. */
export function getLatestAnalysisStatus(project: Project | ProjectSummary): ProjectAnalysisStatus | undefined {
    if ("analyses" in project) {
        return getLatestAnalysis(project).status;
    }
    return project.latestAnalysis?.status;
}

/** Status label and tag color for a project or project summary. */
export function getStatus(project: Project | ProjectSummary): { label: string; color: string } {
    if (project.status === ProjectStatus.DRAFT) return { label: "Draft", color: "default" };
    if (project.closingOutcome) {
        switch (project.closingOutcome) {
            case "signed": return { label: "Signed", color: "green" };
            case "cancelled": return { label: "Cancelled", color: "red" };
            case "completed": return { label: "Complete", color: "green" };
        }
    }
    switch (getLatestAnalysisStatus(project)) {
        case ProjectAnalysisStatus.DRAFT: return { label: "Draft", color: "default" };
        case ProjectAnalysisStatus.SUBMITTED: return { label: "Pending", color: "orange" };
        case ProjectAnalysisStatus.REQUIRE_USER_ACTION: return { label: "Changes requested", color: "red" };
        case ProjectAnalysisStatus.COMPLETED: return { label: "Ready", color: "green" };
        default: return { label: "–", color: "default" };
    }
}

/** Display label for an analysis or analysis summary. */
export function getAnalysisLabel(analysis: ProjectAnalysis | AnalysisSummary): string {
    switch (analysis.type) {
        case AnalysisType.DEAL:
        case AnalysisType.RFX:
            return `Playbook v${analysis.version}`;
        case AnalysisType.ROI:
            return "Savings Report";
        case AnalysisType.COMPLETE:
            return "Complete";
    }
}

export interface SaveAnalysisOutputRequest {
    output: AnalysisOutput;
    // Tell the project's users and move its Updated date. Only read on a save
    // that does not deliver the analysis (a correction on a completed one).
    notify?: boolean;
}

export interface SaveAnalysisFeedbackRequest {
    path: string; // e.g. "report.contractSummary.0"
    value: "good" | "bad";
    comment?: string;
}
